/**
 * The turn timer on the page (owner's request, October 2026). The server runs the clock
 * (backend/game_service.py, sync_turn_timer) and sends with each state `game.turn_timer`:
 * {id (one per turn and per vote), user_id, kind ("turn" | "vote"), remaining (seconds), length (seconds),
 * away}. A delay, not a date:
 * the phone's clock may be off. The page turns it into its own deadline (performance.now()). A state
 * read late gives a deadline too late, never too early: within one clock the earliest deadline seen is
 * kept, and a later one is taken only as time given back (more than 1.5 s, and 31 s left at most: the
 * server brings the time back up to 30 s). Without that, a state read a second late showed « +1 s ».
 */
import { useEffect, useState } from "react";

const SAME = 400; // ms: a deadline that much earlier is a better reading of the same clock
const GIVEN_BACK = 1500;
const GIVEN_BACK_UP_TO = 31000;

/** The clock after a state's `turn_timer` read at `now` (ms): `prev` itself when nothing changed. */
export function nextClock(prev, timer, now) {
  if (!timer?.user_id || timer.remaining == null || !timer.length) return null;
  const { id, user_id: userId, kind, length } = timer;
  const away = !!timer.away;
  const deadline = now + timer.remaining * 1000;
  if (prev && prev.id === id && prev.length === length && prev.away === away) {
    const givenBack = deadline - prev.deadline > GIVEN_BACK && deadline - now <= GIVEN_BACK_UP_TO;
    if (!givenBack && deadline > prev.deadline - SAME) return prev;
  }
  return { id, userId, kind, length, deadline, away };
}

/** {id, userId, kind, length, deadline, away}, or null when nobody is on the clock. */
export function useTurnClock(timer) {
  const [clock, setClock] = useState(null);
  const id = timer?.id;
  const userId = timer?.user_id;
  const kind = timer?.kind;
  const remaining = timer?.remaining;
  const length = timer?.length;
  const away = !!timer?.away;
  useEffect(() => {
    const read = { id, user_id: userId, kind, remaining, length, away };
    setClock((c) => nextClock(c, read, performance.now()));
  }, [id, userId, kind, remaining, length, away]);
  return clock;
}

/** Milliseconds left on the clock (0 once over). */
export const msLeft = (clock) => (clock ? Math.max(0, clock.deadline - performance.now()) : 0);

/**
 * The whole seconds left, re-rendered once a second, only from `from` seconds down (null above,
 * and for no clock): the ring itself drains without React.
 */
export function useSecondsLeft(clock, from) {
  const [secs, setSecs] = useState(null);
  useEffect(() => {
    if (!clock) {
      setSecs(null);
      return undefined;
    }
    let timer;
    const tick = () => {
      const left = msLeft(clock);
      const s = Math.ceil(left / 1000);
      setSecs(s <= from ? s : null);
      if (left <= 0) return;
      // The next whole second, or the moment `from` is reached.
      const wait = s > from ? left - from * 1000 : left - (s - 1) * 1000;
      timer = setTimeout(tick, Math.max(30, wait + 15));
    };
    tick();
    return () => clearTimeout(timer);
  }, [clock, from]);
  return secs;
}
